import React, { useEffect, useState } from 'react';
import { Divider, Box, Flex, Text, ListItem, List } from '@opendoor/bricks/core';

import {
  DigitalCheckoutFormPhoto,
  IPhotoSection,
  photoSections,
} from 'sellDirect/declarations/DigitalCheckoutPhotos';
import { IResetableFormRenderProps } from 'sellDirect/components/closingDash/ResetableForm';
import { PhotoUploadError } from 'sellDirect/components/closingDash/closing/PhotoUploadError';
import { CustomInputLabel } from 'sellDirect/components/closingDash/form/CustomInputLabel';
import { UnderContractOffer, ClosedOffer } from 'sellDirect/OfferContext';
import { FileUploadCard } from 'sellDirect/components/FileUploadCard';
import {
  FileUpload,
  generateS3Key,
  getS3DocumentDownloadUrl,
  S3UploadDestination,
  uploadFileToS3,
} from 'shared/helpers/s3Helpers';
import { createEmptyArray } from 'shared/helpers/utils';
import { useApolloClient } from '@apollo/client';
import { ADMIN_CLIENT_UPLOAD_BUCKET } from 'shared/helpers/globals';

interface IProps {
  offer: UnderContractOffer | ClosedOffer;
  numberOfRetries: number;
  resetableForm: IResetableFormRenderProps;
}

// Header will hide part of the UI on top.
const HEADER_OFFSET = 65;

/**
 * PhotosUpload renders the photo uploading UI for digital checkout form.
 *
 * component-test-check-ignore
 */
export const PhotosUpload: React.FC<IProps> = ({ offer, resetableForm, numberOfRetries }) => {
  const { formState, onUpdate, errorState } = resetableForm;
  const { photos } = formState;
  const photoErrorKeys = Object.keys(errorState?.photos ?? {});
  const firstPhotoError = photoErrorKeys.find((key) => errorState.photos[key]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadCount, setUploadCount] = useState(0);
  const apolloClient = useApolloClient();

  useEffect(() => {
    if (firstPhotoError) {
      let element = document.getElementById(`photo-upload-${firstPhotoError}`);
      if (!element) {
        return;
      }
      let yOffset = -HEADER_OFFSET;
      while (element) {
        yOffset += element.offsetTop;
        element = element.offsetParent as HTMLElement;
      }
      window.scrollTo({ top: yOffset, behavior: 'smooth' });
    }
    // scroll to the error section when either error changes or user retry on the same error.
  }, [firstPhotoError, numberOfRetries]);

  const onAllUploadComplete = async (files: FileUpload[], kind: IPhotoSection['kind']) => {
    const getDownloadUrl = async (s3Key: string) => {
      const res = await getS3DocumentDownloadUrl(apolloClient, ADMIN_CLIENT_UPLOAD_BUCKET, s3Key);
      const url = res.data.seller.s3DocumentDownloadUrl ?? undefined;
      return url;
    };
    onUpdate('photos', [
      ...(formState.photos ?? []),
      ...(await Promise.all(
        files.map(async (file) => ({
          ...file,
          kind,
          downloadUrl: await getDownloadUrl(file.s3Key),
        })),
      )),
    ]);
    setUploading(false);
    setUploadCount((prevCount) => prevCount + files.length);
  };

  const uploadPhoto = (file: FileUpload, onUpdate: (file: FileUpload) => void) => {
    uploadFileToS3(file, apolloClient, false, onUpdate);
  };

  const getS3Key = (file: File): string => {
    return generateS3Key(file, offer.id, S3UploadDestination.DCO);
  };

  return (
    <Box mb={10}>
      <Text fontWeight="bold" fontSize="s3">
        Share move-out photos
      </Text>
      <Text>
        Once the home is empty, share a few photos to complete the sale. If the home has an alarm,
        please make sure it is turned off before leaving.
      </Text>
      <Text fontWeight="semibold" mt={9} mb={3}>
        Tips for taking photos:
      </Text>
      <List variant="bullet" listStylePos="inside">
        <ListItem>Capture images from the corner of the room</ListItem>
        <ListItem>Hold the phone sideways (landscape mode) to capture a wide shot</ListItem>
        <ListItem>Leave closets, cabinets and doors open when you take the photo</ListItem>
        <ListItem>
          Please note that if you leave the page without completing Digital Checkout, your photos
          will not be saved.
        </ListItem>
      </List>
      <Divider my={11} />
      <Flex flexDirection="column" gridGap={10}>
        {photoSections.map(({ title, prompt, kind, numRequired }) => {
          const uploadedPhotos =
            photos?.length > 0
              ? photos?.filter((f: DigitalCheckoutFormPhoto) => f.kind === kind)
              : [];
          const numOfNewUploadsRequired = Math.max(numRequired - uploadedPhotos.length, 0);
          const hasError = errorState?.photos && errorState?.photos[kind];
          return (
            <Box key={kind}>
              <CustomInputLabel
                id={`photo-upload-${kind}`}
                title={title}
                subtitle={prompt}
                required={true}
                titleFontSize="s3"
                titleFontWeight="bold"
                my={3}
              />
              {hasError && (
                <Box mb={4}>
                  <PhotoUploadError numRequired={numRequired} numUploaded={uploadedPhotos.length} />
                </Box>
              )}
              <Flex flexDirection="column" alignItems="start" gridGap={8}>
                {uploadedPhotos.map((photo: DigitalCheckoutFormPhoto) => (
                  <FileUploadCard
                    key={photo.s3Key}
                    accept="image/*"
                    deleteFile={(file: FileUpload) => {
                      const newPhotos = formState.photos.filter(
                        (photo: DigitalCheckoutFormPhoto) => photo.s3Key !== file.s3Key,
                      );
                      onUpdate('photos', newPhotos);
                      // TODO(Yutong): turn on s3 file deletion once confirming the permission on deletion for the directory.
                      // s3DeleteDocument(apolloClient, ADMIN_CLIENT_UPLOAD_BUCKET, photo.s3Key);
                    }}
                    getS3Key={getS3Key}
                    uploadFile={uploadPhoto}
                    uploadedFiles={[photo]}
                    buttonText="Upload photo"
                    modalDisplayTitle={kind}
                  />
                ))}
                {createEmptyArray(numOfNewUploadsRequired).map((_, index) => (
                  <FileUploadCard
                    key={`${kind}-${uploadCount}-${index}`}
                    accept="image/*"
                    onAllUploadComplete={(files) => {
                      onAllUploadComplete(files, kind);
                    }}
                    onUploadStart={() => {
                      setUploading(true);
                    }}
                    getS3Key={getS3Key}
                    uploadFile={uploadPhoto}
                    uploadDisabled={uploading}
                    buttonText="Upload photo"
                    modalDisplayTitle={kind}
                    multiple
                  />
                ))}
                {numOfNewUploadsRequired === 0 && (
                  <FileUploadCard
                    key={`${kind}-new-${uploadCount}`}
                    accept="image/*"
                    onAllUploadComplete={(files) => {
                      onAllUploadComplete(files, kind);
                    }}
                    onUploadStart={() => {
                      setUploading(true);
                    }}
                    getS3Key={getS3Key}
                    uploadFile={uploadPhoto}
                    uploadDisabled={uploading}
                    addAnother
                    buttonText="Upload another photo"
                    modalDisplayTitle={kind}
                    multiple
                  />
                )}
              </Flex>
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
};
